// Painel de informações do grupo / conversa (mockup "Painel Info do Grupo A"):
// título, "Criado por X · data", participantes com online e badge Admin,
// "Adicionar" abre o editar grupo.
import React, { useContext, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import GroupIcon from "@material-ui/icons/Group";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { chatTitle, isGroup, initialsOf, avatarColorFor } from "./chatRules";
import { groupImageUrl } from "./ChatList";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const purpleText = stateText(theme, "#C08BFF", "purple");
  return {
    paper: { width: 480, maxWidth: "calc(100vw - 32px)", margin: 16 },
    header: { padding: "18px 20px", gap: 12 },
    avatar: { width: 44, height: 44, borderRadius: 13, backgroundColor: "rgba(160,68,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: purpleText, overflow: "hidden", flex: "none", "& .MuiSvgIcon-root": { fontSize: 22 }, "& img": { width: "100%", height: "100%", objectFit: "cover" } },
    title: { fontSize: 14, fontWeight: 700, color: theme.palette.text.primary },
    sub: { fontSize: 11.5, color: theme.palette.text.secondary },
    body: { padding: "14px 20px" },
    secRow: { display: "flex", alignItems: "center", marginBottom: 8 },
    secTitle: { fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary },
    add: { marginLeft: "auto", fontSize: 11.5, fontWeight: 600, color: stateText(theme, AEROS.cyan, "info"), background: "none", border: "none", cursor: "pointer", font: "inherit", display: "flex", alignItems: "center", gap: 4, "& .MuiSvgIcon-root": { fontSize: 14 } },
    row: { display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}` },
    avWrap: { position: "relative" },
    av: { width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#07080D" },
    dot: { position: "absolute", bottom: 0, right: 0, width: 8, height: 8, borderRadius: "50%", boxShadow: `0 0 0 2px ${theme.palette.aeros.surface2 || theme.palette.background.paper}` },
    name: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, "& span": { color: theme.palette.aeros.textTertiary, fontWeight: 500 } },
    role: { fontSize: 11, color: theme.palette.text.secondary },
    admin: { fontSize: 10, fontWeight: 600, color: purpleText, backgroundColor: "rgba(160,68,255,0.12)", borderRadius: 5, padding: "1px 7px" },
    desc: { fontSize: 12.5, color: theme.palette.text.secondary, marginBottom: 12, lineHeight: 1.5 },
  };
});

const roleLabel = (u) => (u?.profile === "admin" ? "Administrador" : u?.profile === "supervisor" ? "Supervisor" : "Atendente");

export default function ChatInfoModal({ open, chat, onlineUsers, onClose, onEditGroup }) {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { user: me } = useContext(AuthContext);
  const online = useMemo(() => new Set((onlineUsers || []).filter((u) => u.online).map((u) => Number(u.id))), [onlineUsers]);
  if (!chat) return null;
  const grp = isGroup(chat);
  const members = chat.users || [];
  const img = groupImageUrl(chat);
  const createdBy = chat.owner?.name ? chat.owner.name.split(" ")[0] : null;
  const canEdit = grp && (me.profile === "admin" || Number(chat.ownerId) === Number(me.id));

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form}>
        <div className={`${m.header} ${classes.header}`}>
          <div className={classes.avatar}>{img ? <img src={img} alt="" /> : <GroupIcon />}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.title}>{chatTitle(chat, me.id)}</div>
            <div className={classes.sub}>
              {createdBy ? i18n.t("chatIndex.info.createdBy", { name: createdBy }) : ""}
              {chat.createdAt ? ` · ${new Date(chat.createdAt).toLocaleDateString("pt-BR")}` : ""}
            </div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} aria-label={i18n.t("aeros.header.close")}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.body}>
          {chat.description && <div className={classes.desc}>{chat.description}</div>}
          <div className={classes.secRow}>
            <div className={classes.secTitle}>{i18n.t("chatIndex.info.members", { count: members.length })}</div>
            {canEdit && (
              <button type="button" className={classes.add} onClick={onEditGroup}>
                <PersonAddIcon /> {i18n.t("chatIndex.info.add")}
              </button>
            )}
          </div>
          {members.map((u) => {
            const isMe = Number(u.userId) === Number(me.id);
            const isAdmin = Number(u.userId) === Number(chat.ownerId) || u.user?.profile === "admin";
            return (
              <div key={u.userId} className={classes.row}>
                <div className={classes.avWrap}>
                  <div className={classes.av} style={{ backgroundColor: avatarColorFor(u.userId) }}>{initialsOf(u.user?.name)}</div>
                  <span className={classes.dot} style={{ backgroundColor: online.has(Number(u.userId)) ? AEROS.success : "#5B6376" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={classes.name}>
                    {u.user?.name} {isMe && <span>{i18n.t("aeros.chat.youTag")}</span>}
                  </div>
                  <div className={classes.role}>{roleLabel(u.user)}</div>
                </div>
                {isAdmin && <span className={classes.admin}>{i18n.t("aeros.chat.admin")}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}
